import { getFrameTolerance } from './timeUtils';
import { getFPS, MANUAL_LINE_CONFIDENCE } from './constants';

// Parse JSON asynchronously - uses Web Worker for large payloads to avoid blocking UI
export const parseJSONAsync = (text) => {
  // For small files (<5MB), parse synchronously (Worker overhead not worth it)
  if (text.length < 5 * 1024 * 1024) {
    return Promise.resolve(JSON.parse(text));
  }

  // For large files, parse in a Web Worker to keep UI responsive
  return new Promise((resolve, reject) => {
    const workerCode = `
      self.onmessage = function(e) {
        try {
          const result = JSON.parse(e.data);
          self.postMessage({ ok: true, data: result });
        } catch (err) {
          self.postMessage({ ok: false, error: err.message });
        }
      };
    `;
    const blob = new Blob([workerCode], { type: 'application/javascript' });
    const url = URL.createObjectURL(blob);
    const worker = new Worker(url);

    worker.onmessage = (e) => {
      URL.revokeObjectURL(url);
      worker.terminate();
      if (e.data.ok) {
        resolve(e.data.data);
      } else {
        reject(new Error(e.data.error));
      }
    };

    worker.onerror = (err) => {
      URL.revokeObjectURL(url);
      worker.terminate();
      // Fallback to main thread parsing if Worker fails
      try {
        resolve(JSON.parse(text));
      } catch (parseErr) {
        reject(parseErr);
      }
    };

    worker.postMessage(text);
  });
};

// Last entry of a frame-sorted timeline at or before the given frame, or null when the
// frame precedes every entry. Shared by the in/out badge and its toggle so the key always
// acts on whichever stroke is currently on screen.
export const findEntryAtOrBefore = (timeline, frame) => {
  let lo = 0;
  let hi = timeline.length - 1;
  let match = -1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (timeline[mid].frame <= frame) {
      match = mid;
      lo = mid + 1;
    } else {
      hi = mid - 1;
    }
  }
  return match === -1 ? null : timeline[match];
};

// Apply a hand-made in/out call to a raw strokes-file record.
// The model's original call is captured only on the first correction, so repeated
// toggles never overwrite it with an already-corrected value.
export const applyInOutCorrection = (record, nextIn) => {
  const updated = { ...record, in: nextIn, line_confidence: MANUAL_LINE_CONFIDENCE };
  if (!('original_in' in record)) {
    updated.original_in = record.in ?? null;
    updated.original_line_confidence = record.line_confidence ?? null;
  }
  return updated;
};

// Get color based on annotation type
export const getAnnotationColor = (type) => {
  switch (type) {
    case 'stroke': return '#ffb86c';
    case 'bounce': return '#8be9fd';
    case 'in-flight': return '#ff79c6';
    case 'net': return '#bd93f9';
    default: return '#f8f8f2';
  }
};

// Check if two timestamps are at the same frame
export const isSameFrame = (timestamp1, timestamp2, fps) => {
  if (!fps) {
    throw new Error('FPS is required for isSameFrame');
  }
  const tolerance = getFrameTolerance(fps);
  return Math.abs(timestamp1 - timestamp2) < tolerance;
};

// Find annotation at current time
export const findAnnotationAtTime = (annotations, currentTime, fps) => {
  if (!fps) {
    throw new Error('FPS is required for findAnnotationAtTime');
  }
  const tolerance = getFrameTolerance(fps);
  return annotations.find(ann => Math.abs(ann.timestamp - currentTime) < tolerance);
};

// Get current player sides based on timeline position
export const getCurrentPlayerSides = (sideSwitches, time) => {
  const previousSwitches = sideSwitches
    .filter(sw => sw.timestamp <= time)
    .sort((a, b) => b.timestamp - a.timestamp);
  
  if (previousSwitches.length === 0) {
    return { top: 'red', bottom: 'green' };
  }
  
  const lastSwitch = previousSwitches[0];
  return {
    top: lastSwitch.topPlayer,
    bottom: lastSwitch.topPlayer === 'red' ? 'green' : 'red'
  };
};

// Convert video coordinates between native and display
export const convertVideoCoordinates = (x, y, nativeWidth, nativeHeight, displayWidth = 1280, displayHeight = 720) => {
  const displayX = (x / nativeWidth) * displayWidth;
  const displayY = (y / nativeHeight) * displayHeight;
  return { x: displayX, y: displayY };
};

// Convert bounding box coordinates from native to display
export const convertBoundingBoxCoordinates = (x1, y1, x2, y2, nativeWidth, nativeHeight, displayWidth = 1280, displayHeight = 720) => {
  const { x: displayX1, y: displayY1 } = convertVideoCoordinates(x1, y1, nativeWidth, nativeHeight, displayWidth, displayHeight);
  const { x: displayX2, y: displayY2 } = convertVideoCoordinates(x2, y2, nativeWidth, nativeHeight, displayWidth, displayHeight);
  
  return {
    x1: displayX1,
    y1: displayY1,
    x2: displayX2,
    y2: displayY2
  };
};

// Convert display coordinates to native video coordinates
export const convertToNativeCoordinates = (displayX, displayY, nativeWidth, nativeHeight, displayWidth = 1280, displayHeight = 720) => {
  const nativeX = (displayX / displayWidth) * nativeWidth;
  const nativeY = (displayY / displayHeight) * nativeHeight;
  return { x: nativeX, y: nativeY };
};

// Count annotations by type - single pass O(n) instead of 4 separate filter passes
export const countAnnotationsByType = (annotations) => {
  const counts = { stroke: 0, bounce: 0, inFlight: 0, net: 0 };
  for (const ann of annotations) {
    switch (ann.type) {
      case 'stroke': counts.stroke++; break;
      case 'bounce': counts.bounce++; break;
      case 'in-flight': counts.inFlight++; break;
      case 'net': counts.net++; break;
    }
  }
  return counts;
};

// Calculate annotation progress (current/total) for each type - single pass
export const getAnnotationProgress = (annotations, currentTime, playerFilter = 'all', strokeTypeFilter = 'all') => {
  const total = { stroke: 0, bounce: 0, inFlight: 0, net: 0 };
  const current = { stroke: 0, bounce: 0, inFlight: 0, net: 0 };

  for (const ann of annotations) {
    // Apply player filter
    if (playerFilter !== 'all' && ann.player !== playerFilter) continue;
    // Apply stroke type filter (only for stroke annotations)
    if (strokeTypeFilter !== 'all' && ann.type === 'stroke' && ann.strokeType !== strokeTypeFilter) continue;

    const beforeCurrent = ann.timestamp <= currentTime;

    switch (ann.type) {
      case 'stroke':
        total.stroke++;
        if (beforeCurrent) current.stroke++;
        break;
      case 'bounce':
        total.bounce++;
        if (beforeCurrent) current.bounce++;
        break;
      case 'in-flight':
        total.inFlight++;
        if (beforeCurrent) current.inFlight++;
        break;
      case 'net':
        total.net++;
        if (beforeCurrent) current.net++;
        break;
    }
  }

  return {
    stroke: { current: current.stroke, total: total.stroke },
    bounce: { current: current.bounce, total: total.bounce },
    inFlight: { current: current.inFlight, total: total.inFlight },
    net: { current: current.net, total: total.net },
  };
};

// Get next/previous annotation of specific type
export const getNavigationAnnotation = (annotations, currentTime, type, direction = 'next', playerFilter = 'all', strokeTypeFilter = 'all', volleyStrokeIds = null) => {
  let filtered = annotations.filter(a => a.type === type);
  
  // Apply player filter if specified
  if (playerFilter !== 'all') {
    filtered = filtered.filter(a => a.player === playerFilter);
  }
  
  // Apply stroke type filter if specified (only for stroke annotations)
  if (strokeTypeFilter !== 'all' && type === 'stroke') {
    filtered = filtered.filter(a => a.strokeType === strokeTypeFilter);
  }

  // Apply volley filter if specified (only for stroke annotations)
  if (volleyStrokeIds !== null && type === 'stroke') {
    filtered = filtered.filter(a => volleyStrokeIds.has(a.id));
  }
  
  const sorted = direction === 'next' 
    ? filtered.filter(a => a.timestamp > currentTime).sort((a, b) => a.timestamp - b.timestamp)
    : filtered.filter(a => a.timestamp < currentTime).sort((a, b) => b.timestamp - a.timestamp);
  
  return sorted[0] || null;
};

// Returns a Set of annotation IDs for strokes with no bounce before the next stroke.
// A stroke is a "volley" when the next relevant event (stroke or bounce) in the
// sequence is another stroke — i.e. no bounce lands between the two strokes.
// A net hit between strokes also disqualifies — the ball didn't bounce, but it
// hit the net rather than being volleyed cleanly.
export const getVolleyStrokeIds = (annotations) => {
  const sorted = [...annotations].sort((a, b) => a.timestamp - b.timestamp);
  const ids = new Set();
  for (let i = 0; i < sorted.length; i++) {
    if (sorted[i].type !== 'stroke') continue;
    for (let j = i + 1; j < sorted.length; j++) {
      const next = sorted[j];
      if (next.type === 'bounce') break;        // bounce found — not a volley
      if (next.type === 'net') break;           // net hit — not a volley
      if (next.type === 'stroke') { ids.add(sorted[i].id); break; } // no bounce/net before next stroke — volley
    }
  }
  return ids;
}; 